import { DRUID_PATH } from '@somnhold/content';
import { hexToWorld, type Hex } from '../hex.ts';
import { idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';
import type { BattleState, DeepMark, DeepRules, DeepSapNode, DeepState, Enemy, Hero, RootsState } from '../types.ts';
import { damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';
import { rootsAdvance, rootsCut, rootsSow, type RootEvent, type RootNet } from './druid-layout.ts';
import { sapFlow } from './druid-winter-layout.ts';
import type { ChamberLayout, MouthsLayout } from './druid-roots-layout.ts';

/**
 * Inside the Grandsire (docs/sendas/druida.md, chapter 5; the grounds in battle/druid-roots-layout.ts).
 *
 * - The buds below (act 2, `mouths`; the lessons of chapters 4 and 1): the sap comes down the gallery in a pulse every
 *   3 s and splits even at each fork among the branches whose knot is open; a mouth stopped with rot opens for good on a
 *   pulse that gives it what it asks, and more cracks it (it spits rot, a slime comes out, shut a while). Under an open
 *   mouth its sound roots wake and grow toward the nearest moss the hero sows (the bridge's rule); two at one stone too
 *   close together tangle (cut the knot); both in the mouth and it is clean. The rot drips from the roof on the hero;
 *   rotten roots (rootwights) crawl out of the mouths still shut, for Ceda or the hero.
 * - The Carcoma (act 4, the path's enemy, §5): it takes nothing while its four roots feed it, a quarter more of every
 *   blow for each one cut. Each root is cut by its lesson: the mud one by two sound roots sown in the order that doesn't
 *   tangle; the resin one by fire lit upwind of it with Hob's ember; the hidden one by rooting on a cell where only the
 *   rot runs under the wood (the mushrooms say); the frost one by giving its three buds their sap on one pulse. With
 *   four or three it spits splinters in a cone; with two or one it crawls at the hero, biting, leaving rot that slows;
 *   bare, it goes for the colliers at the door, held a while at each of Elvira's knots the hero touches; a root grows
 *   back after a while (sooner with the Grandsire healed, whose branch squeezes it; later with it let go, the seed to
 *   keep from its swipes), until it is down to a quarter. Slimes out of the walls go for the colliers, who chop them.
 *
 * Cost: a handful of points a tick; a flow's split once a pulse over a dozen nodes; the roots' rule once a tick over two
 * tips (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

type St = DeepState;
type Pt = { x: number; y: number };
type Mouths = NonNullable<DeepRules['mouths']>;
type Car = NonNullable<DeepRules['carcoma']>;

const fresh = (): St => ({
  done: -1,
  lost: 0,
  t: 0,
  job: -1,
  held: 0,
  pend: [],
  roots: [],
  clean: [],
  sows: 0,
  tangles: 0,
  closed: [],
  buds: [],
  crackUntil: [],
  pulseNext: 0,
  pulses: 0,
  turns: 0,
  cracks: 0,
  open: [],
  drips: [],
  dripNext: 0,
  dripN: 0,
  slimeNext: 0,
  boss: 0,
  cut: [],
  cuts: 0,
  wrongs: 0,
  resin: [],
  catchAt: [],
  wind: 1,
  hcut: [],
  bare: -1,
  grow: -1,
  growAt: -1,
  regrows: 0,
  cone: -1,
  cx: 0,
  cy: 0,
  coneNext: 0,
  bite: -1,
  bx: 0,
  by: 0,
  biteNext: 0,
  trail: [],
  trailNext: 0,
  gateUsed: [],
  gate: -1,
  gateUntil: -1,
  seedHits: 0,
  swipe: -1,
  sx: 0,
  sy: 0,
  swipeNext: 0,
  branchNext: 0,
  sl: {},
  chopNext: 0,
  rotR: 0,
});

const rootsOf = (net: { nodes: unknown[] }): RootsState => ({
  tips: [0, 1].map((n) => ({ at: n, prev: n, to: -1, t0: 0, arr: 0, mode: 0 as const, follow: -1, trail: [n] })),
  sprout: 1 << 2,
  hit: net.nodes.flatMap(() => [-1, -1]),
  knot: -1,
});

const netPts = (N: RootNet): { nodes: Pt[]; edge: number[][] } => ({ nodes: N.nodes.map((h: Hex) => hexToWorld(h.q, h.r)), edge: N.edge.map((e) => [...e]) });
const sapCopy = (nodes: readonly DeepSapNode[]): DeepSapNode[] => nodes.map((n) => ({ x: n.x, y: n.y, parent: n.parent, kids: [...n.kids], need: n.need, knot: n.knot, stage: n.stage }));
const grow = (): DeepRules['grow'] => ({ per: DRUID_PATH.mouths.per, see: DRUID_PATH.mouths.see, near: DRUID_PATH.mouths.near });

/* ---------- rules from the layouts (the fight's builder in campaign/sendas.ts and the drawing build them the same) ---------- */

export const mouthsRules = (L: MouthsLayout): DeepRules => {
  const P = DRUID_PATH.mouths;
  const marks: DeepMark[] = [];
  L.knots.forEach((k, i) => marks.push({ k: 'knot', i, j: 0, x: k.x, y: k.y }));
  L.nets.forEach((m, i) => {
    m.net.nodes.forEach((h, j) => {
      if (j < 3) return;
      const p = hexToWorld(h.q, h.r);
      marks.push({ k: 'stone', i, j, x: p.x, y: p.y });
    });
    const at = L.nodes[L.mouths[i] as number] as Pt;
    marks.push({ k: 'tangle', i, j: 0, x: at.x, y: at.y });
  });
  return {
    kind: 'mouths',
    marks,
    grow: grow(),
    nets: L.nets.map((m) => netPts(m.net)),
    mouths: {
      nodes: sapCopy(L.nodes),
      knots: L.knots.map((k) => ({ ...k })),
      mouths: [...L.mouths],
      foot: { ...L.foot },
      pulse: P.pulse,
      crack: P.crack,
      spit: { ...P.spit },
      cut: P.cut,
      reach: P.reach,
      biteBp: P.biteBp,
      throw: P.throw,
      sow: P.sow,
      knot: P.knot,
      drip: { ...P.drip },
      slimes: { ...P.slimes },
    },
  };
};

export const carcomaRules = (L: ChamberLayout, hp: number, healed: boolean, pre: number, seed: number): DeepRules => {
  const P = DRUID_PATH.carcoma;
  const marks: DeepMark[] = [];
  L.mud.nodes.forEach((h, j) => {
    if (j < 3) return;
    const p = hexToWorld(h.q, h.r);
    marks.push({ k: 'stone', i: 0, j, x: p.x, y: p.y });
  });
  const isl = hexToWorld((L.mud.nodes[2] as Hex).q, (L.mud.nodes[2] as Hex).r);
  marks.push({ k: 'tangle', i: 0, j: 0, x: isl.x, y: isl.y });
  L.patches.forEach((p, i) => i !== L.rootAt && marks.push({ k: 'patch', i, j: 0, x: p.x, y: p.y }));
  L.hidden.cells.forEach((h, i) => {
    if ((L.hidden.black[i] as number) + (L.hidden.white[i] as number) === 0) return;
    const p = hexToWorld(h.q, h.r);
    marks.push({ k: 'cell', i, j: 0, x: p.x, y: p.y });
  });
  L.frost.knots.forEach((k, i) => marks.push({ k: 'knot', i, j: 0, x: k.x, y: k.y }));
  L.gates.forEach((g, i) => marks.push({ k: 'gate', i, j: 0, x: g.at.x, y: g.at.y }));
  // The order the roots grow back in: from the seed, each once before any again.
  const order = [0, 1, 2, 3];
  const rng = seedRng(deriveSeed(seed, 0x9e60));
  for (let i = 3; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = order[i] as number;
    order[i] = order[j] as number;
    order[j] = t;
  }
  return {
    kind: 'carcoma',
    marks,
    grow: grow(),
    nets: [netPts(L.mud)],
    carcoma: {
      hp,
      healed,
      pre: imin(2, imax(0, pre)),
      mid: { ...L.mid },
      door: { ...L.door },
      walls: L.roots.map((r) => ({ ...r.wall })),
      patches: L.patches.map((p) => ({ ...p })),
      rootAt: L.rootAt,
      wind: L.wind,
      hidden: { cells: L.hidden.cells.map((h) => hexToWorld(h.q, h.r)), rot: [...L.hidden.rot], black: [...L.hidden.black], white: [...L.hidden.white], vis: [...L.hidden.vis] },
      frost: { nodes: sapCopy(L.frost.nodes), knots: L.frost.knots.map((k) => ({ ...k })), spring: { ...L.frost.spring }, buds: [...L.frost.buds] },
      gates: L.gates.map((g) => ({ at: { ...g.at }, bar: { ...g.bar } })),
      holes: L.holes.map((p) => ({ ...p })),
      regrowOrder: order,
      takenBp: [...P.takenBp],
      regrow: { at: healed ? P.regrow.healed : P.regrow.released, thread: P.regrow.thread, lastBp: P.regrow.lastBp },
      resin: { ...P.resin },
      cut: P.cut,
      reach: P.reach,
      wrongBp: P.wrongBp,
      chillBp: P.chillBp,
      pulse: P.pulse,
      knot: P.knot,
      cone: { ...P.cone },
      crawl: P.crawl.pace,
      tether: P.crawl.tether,
      trail: { ...P.trail },
      bite: { ...P.bite },
      run: P.run,
      doorR: P.door,
      gate: P.gate,
      touch: P.touch,
      slimes: { ...P.slimes, every: healed ? P.wallsEvery.healed : P.wallsEvery.released },
      chop: { ...P.chop },
      branch: { ...P.healed },
      swipe: { ...P.released },
      seed: P.seed,
      rot: { ...P.rot, r: (L.radius - 1) * 1000 },
    },
  };
};

/* ---------- helpers ---------- */

const heroOf = (s: BattleState): Hero | undefined => s.heroes.find((x) => x.downTicks === 0);
const MUD = 0;
const RESIN = 1;
const HIDDEN = 2;
const FROST = 3;
const CRACK = DRUID_PATH.mouths.crack;

const spawn = (s: BattleState, kind: 'slime' | 'rootwight', x: number, y: number): Enemy => {
  const e = spawnEnemy(s, kind, 1, false, 0, x, y, 0);
  if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  return e;
};

/** The roots' events as the board's: a root grown to a node, tangled, braided, home. */
const rootEvents = (s: BattleState, net: { nodes: Pt[] }, i: number, out: RootEvent[], w: St, cue: (c: string) => void): void => {
  for (const e of out) {
    const p = net.nodes[e.node] as Pt;
    ev(s, { t: 'senda', kind: e.k === 'grow' ? 'root' : e.k === 'back' ? 'knotCut' : e.k, npc: `${i}:${e.root}`, x: p.x, y: p.y, n: e.node });
    if (e.k === 'tangle') {
      w.tangles++;
      cue(`tangle${w.tangles}`);
    }
  }
};

/** A flow's pulse over its buds (a mouth or a bud: a node that asks): open, short or cracked; returns the nodes that opened now. */
const flowPulse = (s: BattleState, nodes: DeepSapNode[], knots: { node: number }[], w: St, skip: (n: number) => boolean): { opened: number[]; cracked: number[] } => {
  const flow = sapFlow(nodes, knots, 0, w.closed.map((x) => x === 1));
  const opened: number[] = [];
  const cracked: number[] = [];
  w.pulses++;
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i] as DeepSapNode;
    if (!n.need || skip(i)) continue;
    const f = flow[i] as number;
    if ((w.crackUntil[i] as number) > s.tick) {
      if (f === 1) w.crackUntil[i] = s.tick + CRACK;
      w.buds[i] = 1;
      continue;
    }
    w.buds[i] = f;
    if (f === 1) {
      w.crackUntil[i] = s.tick + CRACK;
      w.cracks++;
      cracked.push(i);
    } else if (f === 0) opened.push(i);
  }
  return { opened, cracked };
};

/** Working at a mark: within `d`, standing still `need` ticks. True the tick it is done (the job cleared). */
const workAt = (s: BattleState, w: St, h: Hero, m: DeepMark, d: number, need: number): boolean => {
  if (h.moving || !withinRange(h.x, h.y, m.x, m.y, d)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return false;
  }
  if (++w.held < need) return false;
  w.job = -1;
  w.held = 0;
  return true;
};

/* ---------- act 2: the buds below ---------- */

const mouthsTick = (s: BattleState, r: DeepRules, M: Mouths, w: St, cue: (c: string) => void): void => {
  const h = heroOf(s);
  if (w.done >= 0) return;
  if (!w.closed.length) {
    w.closed = M.knots.map(() => 0);
    w.buds = M.nodes.map((n) => (n.need ? -1 : -2));
    w.crackUntil = M.nodes.map(() => 0);
    w.open = M.mouths.map(() => -1);
    w.roots = r.nets.map((n) => rootsOf(n));
    w.clean = r.nets.map(() => -1);
    w.pulseNext = M.pulse;
    w.dripNext = M.drip.first;
    w.slimeNext = M.slimes.first;
  }
  // The hero at a mark.
  const m = w.job >= 0 ? r.marks[w.job] : undefined;
  if (h && m) {
    if (m.k === 'knot' && workAt(s, w, h, m, M.reach, M.cut)) {
      w.closed[m.i] = w.closed[m.i] ? 0 : 1;
      w.turns++;
      ev(s, { t: 'senda', kind: 'knot', npc: w.closed[m.i] ? 'shut' : 'open', x: m.x, y: m.y, n: m.i });
      damageHero(s, h, mulBp(h.stats.maxHp, M.biteBp), null, 'poison', true);
      cue(`turn${w.turns}`);
    } else if (m.k === 'stone') {
      const st = w.roots[m.i] as RootsState;
      if ((w.open[m.i] as number) < 0 || (w.clean[m.i] as number) >= 0 || st.sprout & (1 << m.j)) w.job = -1;
      else if (workAt(s, w, h, m, M.sow, M.throw)) {
        rootsSow(st, m.j);
        w.sows++;
        ev(s, { t: 'senda', kind: 'sow', npc: String(m.i), x: m.x, y: m.y, n: m.j });
        cue(`sow${w.sows}`);
      }
    } else if (m.k === 'tangle') {
      const st = w.roots[m.i] as RootsState;
      const at = st.knot >= 0 ? ((r.nets[m.i] as { nodes: Pt[] }).nodes[st.knot] as Pt) : null;
      if (!at) w.job = -1;
      else if (workAt(s, w, h, { ...m, x: at.x, y: at.y }, M.reach + 300, M.knot)) {
        rootsCut(st);
        ev(s, { t: 'senda', kind: 'knotCut', npc: String(m.i), x: at.x, y: at.y });
        damageHero(s, h, mulBp(h.stats.maxHp, M.biteBp), null, 'poison', true);
        cue('knotCut');
      }
    }
  }
  // The sap's pulse.
  if (s.tick >= w.pulseNext) {
    w.pulseNext = s.tick + M.pulse;
    const { opened, cracked } = flowPulse(s, M.nodes, M.knots, w, (n) => {
      const k = M.mouths.indexOf(n);
      return k >= 0 && (w.open[k] as number) >= 0;
    });
    ev(s, { t: 'senda', kind: 'pulse', npc: '', x: M.foot.x, y: M.foot.y, n: w.pulses });
    for (const n of opened) {
      const k = M.mouths.indexOf(n);
      if (k < 0) continue;
      w.open[k] = s.tick;
      const p = M.nodes[n] as Pt;
      ev(s, { t: 'senda', kind: 'mouthOpen', npc: '', x: p.x, y: p.y, n: k });
      cue(`open${w.open.filter((x) => x >= 0).length}`);
    }
    for (const n of cracked) {
      const p = M.nodes[n] as Pt;
      ev(s, { t: 'senda', kind: 'mouthCrack', npc: '', x: p.x, y: p.y, n: M.mouths.indexOf(n) });
      cue(`crack${w.cracks}`);
      for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, p.x, p.y, M.spit.r)) damageHero(s, x, mulBp(x.stats.maxHp, M.spit.bp), null, 'poison', true);
      spawn(s, 'rootwight', p.x, p.y + 400);
    }
  }
  // The roots under each open mouth.
  for (let i = 0; i < r.nets.length; i++) {
    if ((w.open[i] as number) < 0 || (w.clean[i] as number) >= 0) continue;
    const st = w.roots[i] as RootsState;
    const net = r.nets[i] as { nodes: Pt[]; edge: number[][] };
    const out: RootEvent[] = [];
    rootsAdvance(net as unknown as RootNet, r.grow, st, s.tick, out);
    rootEvents(s, net, i, out, w, cue);
    if (st.tips.every((t) => t.mode === 3)) {
      w.clean[i] = s.tick;
      const p = net.nodes[2] as Pt;
      ev(s, { t: 'senda', kind: 'mouthClean', npc: '', x: p.x, y: p.y, n: i });
      cue(`clean${w.clean.filter((x) => x >= 0).length}`);
    }
  }
  // The rot dripping from the roof on the hero.
  if (h && s.waveT >= M.drip.first && s.tick >= w.dripNext) {
    w.dripNext = s.tick + M.drip.every;
    w.drips.push([h.x, h.y, s.tick + M.drip.warn]);
    w.dripN++;
    ev(s, { t: 'senda', kind: 'dripWarn', npc: '', x: h.x, y: h.y, n: M.drip.warn });
    cue(`drip${w.dripN}`);
  }
  for (let i = w.drips.length - 1; i >= 0; i--) {
    const d = w.drips[i] as number[];
    if (s.tick < (d[2] as number)) continue;
    w.drips.splice(i, 1);
    ev(s, { t: 'senda', kind: 'drip', npc: '', x: d[0] as number, y: d[1] as number });
    for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, d[0] as number, d[1] as number, M.drip.r)) damageHero(s, x, mulBp(x.stats.maxHp, M.drip.bp), null, 'poison', true);
  }
  // Slimes out of the mouths still shut.
  if (s.waveT >= M.slimes.first && s.tick >= w.slimeNext) {
    w.slimeNext = s.tick + M.slimes.every;
    const shut = M.mouths.filter((_, k) => (w.open[k] as number) < 0);
    const from = shut.length ? shut : M.mouths;
    for (let k = 0; k < M.slimes.n; k++) {
      const p = M.nodes[from[(w.slimeNext + k) % from.length] as number] as Pt;
      spawn(s, 'rootwight', p.x + (k % 2 ? 300 : -300), p.y + 500);
    }
  }
  if (w.clean.every((x) => x >= 0)) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'galleryClean', npc: '', x: M.foot.x, y: M.foot.y });
    cue('clean');
  }
};

/* ---------- act 4: the Carcoma ---------- */

/** Roots still feeding her. */
const feeding = (w: St): number => w.cut.filter((x) => !x).length;

/** A root's lesson set back as it was (it grew again): the mud's roots, the resin, the hidden cells, the frost's knots. */
const resetRoot = (s: BattleState, r: DeepRules, C: Car, w: St, k: number): void => {
  if (k === MUD) w.roots[0] = rootsOf(r.nets[0] as { nodes: Pt[] });
  if (k === RESIN) {
    w.resin = C.patches.map(() => 0);
    w.catchAt = C.patches.map(() => -1);
    // (The air in the chamber turns with the root: the smoke shows it again.)
    w.wind = rngInt(s.rng, 2) === 0 ? 1 : -1;
  }
  if (k === HIDDEN) w.hcut = C.hidden.cells.map(() => 0);
  if (k === FROST) {
    w.closed = C.frost.knots.map(() => 0);
    w.buds = C.frost.nodes.map((n) => (n.need ? -1 : -2));
    w.crackUntil = C.frost.nodes.map(() => 0);
  }
};

const cutRoot = (s: BattleState, C: Car, w: St, k: number, cue: (c: string) => void): void => {
  if (w.cut[k]) return;
  w.cut[k] = 1;
  w.cuts++;
  const p = C.walls[k] as Pt;
  ev(s, { t: 'senda', kind: 'rootCut', npc: ['mud', 'resin', 'hidden', 'frost'][k] as string, x: p.x, y: p.y, n: k });
  cue(`cut${w.cuts}`);
  cue(`cut_${['mud', 'resin', 'hidden', 'frost'][k]}`);
  if (feeding(w) === 0) {
    w.bare = s.tick;
    w.gateUsed = C.gates.map(() => 0);
    const b = findEnemy(s, w.boss);
    ev(s, { t: 'senda', kind: 'bare', npc: '', x: b?.x ?? C.mid.x, y: b?.y ?? C.mid.y });
    cue(`bare${w.regrows + 1}`);
  }
};

const carcomaTick = (s: BattleState, r: DeepRules, C: Car, w: St, cue: (c: string) => void): void => {
  const h = heroOf(s);
  const boss = findEnemy(s, w.boss);
  if (w.done >= 0 || w.lost) return;
  if (!boss || boss.hp <= 0) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'carcomaDown', npc: '', x: boss?.x ?? C.mid.x, y: boss?.y ?? C.mid.y });
    cue('down');
    return;
  }
  // The hero at a mark.
  const m = w.job >= 0 ? r.marks[w.job] : undefined;
  if (h && m) {
    if (m.k === 'stone') {
      const st = w.roots[0] as RootsState;
      if (w.cut[MUD] || st.sprout & (1 << m.j)) w.job = -1;
      else if (workAt(s, w, h, m, C.resin.reach, C.resin.throw)) {
        rootsSow(st, m.j);
        w.sows++;
        ev(s, { t: 'senda', kind: 'sow', npc: '0', x: m.x, y: m.y, n: m.j });
        cue(`sow${w.sows}`);
      }
    } else if (m.k === 'tangle') {
      const st = w.roots[0] as RootsState;
      const at = st.knot >= 0 ? ((r.nets[0] as { nodes: Pt[] }).nodes[st.knot] as Pt) : null;
      if (!at || w.cut[MUD]) w.job = -1;
      else if (workAt(s, w, h, { ...m, x: at.x, y: at.y }, C.reach + 300, C.knot)) {
        rootsCut(st);
        ev(s, { t: 'senda', kind: 'knotCut', npc: '0', x: at.x, y: at.y });
        damageHero(s, h, mulBp(h.stats.maxHp, C.chillBp), null, 'poison', true);
        cue('knotCut');
      }
    } else if (m.k === 'patch') {
      if (w.cut[RESIN] || w.resin[m.i] !== 0) w.job = -1;
      else if (workAt(s, w, h, m, C.resin.reach, C.resin.throw)) {
        w.resin[m.i] = s.tick + C.resin.burn;
        w.catchAt[m.i] = s.tick + C.resin.catch;
        ev(s, { t: 'senda', kind: 'ignite', npc: 'hero', x: m.x, y: m.y, n: m.i });
        cue('ignite');
      }
    } else if (m.k === 'cell') {
      if (w.cut[HIDDEN] || w.hcut[m.i]) w.job = -1;
      else if (workAt(s, w, h, m, C.reach, C.cut)) {
        w.hcut[m.i] = 1;
        const sound = (C.hidden.white[m.i] as number) > 0;
        const rot = !!C.hidden.rot[m.i];
        ev(s, { t: 'senda', kind: 'cellCut', npc: sound ? 'bad' : rot ? 'good' : 'vain', x: m.x, y: m.y, n: m.i });
        if (sound) {
          w.wrongs++;
          damageHero(s, h, mulBp(h.stats.maxHp, C.wrongBp), null, 'poison', true);
          cue(`wrong${w.wrongs}`);
        } else if (rot) cutRoot(s, C, w, HIDDEN, cue);
      }
    } else if (m.k === 'knot') {
      if (w.cut[FROST]) w.job = -1;
      else if (workAt(s, w, h, m, C.reach, C.cut)) {
        w.closed[m.i] = w.closed[m.i] ? 0 : 1;
        w.turns++;
        ev(s, { t: 'senda', kind: 'knot', npc: w.closed[m.i] ? 'shut' : 'open', x: m.x, y: m.y, n: m.i });
        damageHero(s, h, mulBp(h.stats.maxHp, C.chillBp), null, 'frost', true);
        cue(`turn${w.turns}`);
      }
    } else if (m.k === 'gate') {
      if (w.bare < 0 || w.gateUsed[m.i]) w.job = -1;
      else if (workAt(s, w, h, m, C.reach + 300, C.touch)) {
        w.gateUsed[m.i] = 1;
        w.gate = m.i;
        w.gateUntil = s.tick + C.gate;
        const g = C.gates[m.i] as { bar: Pt };
        ev(s, { t: 'senda', kind: 'gate', npc: 'elvira', x: g.bar.x, y: g.bar.y, n: m.i });
        cue(`gate${w.gateUsed.filter(Boolean).length}`);
      }
    }
  }
  // Mud: the sound roots toward the moss.
  if (!w.cut[MUD]) {
    const st = w.roots[0] as RootsState;
    const net = r.nets[0] as { nodes: Pt[]; edge: number[][] };
    const out: RootEvent[] = [];
    rootsAdvance(net as unknown as RootNet, r.grow, st, s.tick, out);
    rootEvents(s, net, 0, out, w, cue);
    if (st.tips.every((t) => t.mode === 3)) cutRoot(s, C, w, MUD, cue);
  }
  // Resin: the fire the air's way, a patch at a time; the root's patch alight burns it.
  if (!w.cut[RESIN]) {
    let burning = false;
    for (let i = 0; i < C.patches.length; i++) {
      const b = w.resin[i] as number;
      if (b <= 0) continue;
      burning = true;
      if (i === C.rootAt) cutRoot(s, C, w, RESIN, cue);
      if ((w.catchAt[i] as number) >= 0 && s.tick >= (w.catchAt[i] as number)) {
        w.catchAt[i] = -1;
        const j = i + w.wind;
        if (j >= 0 && j < C.patches.length && w.resin[j] === 0) {
          w.resin[j] = s.tick + C.resin.burn;
          w.catchAt[j] = s.tick + C.resin.catch;
          const p = C.patches[j] as Pt;
          ev(s, { t: 'senda', kind: 'ignite', npc: '', x: p.x, y: p.y, n: j });
        }
      }
      if (s.tick >= b) w.resin[i] = -1;
    }
    // Nothing left upwind of the root to light: the resin sweats out again (never stuck for good).
    if (!burning && !w.cut[RESIN]) {
      let any = false;
      for (let i = 0; i < C.patches.length; i++) if (i !== C.rootAt && w.resin[i] === 0 && (i - C.rootAt) * w.wind < 0) any = true;
      if (!any) {
        w.resin = C.patches.map(() => 0);
        w.catchAt = C.patches.map(() => -1);
        ev(s, { t: 'senda', kind: 'resinBack', npc: '', x: (C.patches[C.rootAt] as Pt).x, y: (C.patches[C.rootAt] as Pt).y });
      }
    }
  }
  // (A patch alight burns whoever stands in it, the root cut or not.)
  if (s.tick % 12 === 0)
    for (let i = 0; i < C.patches.length; i++) {
      if ((w.resin[i] as number) <= 0) continue;
      const p = C.patches[i] as Pt;
      for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, p.x, p.y, 600)) damageHero(s, x, mulBp(x.stats.maxHp, C.resin.heatBp), null, 'fire', true);
    }
  // Frost: its pulse; all three buds open on one and the root melts.
  if (!w.cut[FROST] && s.tick >= w.pulseNext) {
    w.pulseNext = s.tick + C.pulse;
    const { opened, cracked } = flowPulse(s, C.frost.nodes, C.frost.knots, w, () => false);
    const sp = C.frost.spring;
    ev(s, { t: 'senda', kind: 'pulse', npc: '', x: sp.x, y: sp.y, n: w.pulses });
    for (const n of cracked) {
      const p = C.frost.nodes[n] as Pt;
      ev(s, { t: 'senda', kind: 'budCrack', npc: '', x: p.x, y: p.y, n });
      cue(`crack${w.cracks}`);
    }
    void opened;
    if (C.frost.buds.every((b) => w.buds[b] === 0)) cutRoot(s, C, w, FROST, cue);
  }
  const left = feeding(w);
  const low = boss.hp <= mulBp(boss.maxHp, C.regrow.lastBp);
  if (low && !w.pend.includes('last') && !(s.senda?.cues ?? []).includes('last')) cue('last');
  // Bare: a root grows back (the thread on the wall first), unless she is down to a quarter.
  if (left === 0 && w.grow < 0 && !low) {
    w.grow = C.regrowOrder[w.regrows % 4] as number;
    w.growAt = w.bare + C.regrow.at;
  }
  if (w.grow >= 0 && low && w.growAt - s.tick > C.regrow.thread) {
    w.grow = -1;
    w.growAt = -1;
  }
  if (w.grow >= 0) {
    if (s.tick === w.growAt - C.regrow.thread) {
      const p = C.walls[w.grow] as Pt;
      ev(s, { t: 'senda', kind: 'thread', npc: ['mud', 'resin', 'hidden', 'frost'][w.grow] as string, x: p.x, y: p.y, n: C.regrow.thread });
      cue(`thread${w.regrows + 1}`);
    }
    if (s.tick >= w.growAt) {
      const k = w.grow;
      w.cut[k] = 0;
      w.cuts--;
      resetRoot(s, r, C, w, k);
      w.grow = -1;
      w.growAt = -1;
      w.bare = -1;
      w.regrows++;
      w.gate = -1;
      // Pulled back to the middle by it.
      boss.x = C.mid.x;
      boss.y = C.mid.y;
      w.trail = [];
      const p = C.walls[k] as Pt;
      ev(s, { t: 'senda', kind: 'regrow', npc: ['mud', 'resin', 'hidden', 'frost'][k] as string, x: p.x, y: p.y, n: k });
      cue(`regrow${w.regrows}`);
    }
  }
  // Splinters (four or three roots): a cone at the hero.
  if (h && left >= 3 && w.cone < 0 && s.waveT >= C.cone.first && s.tick >= w.coneNext) {
    w.coneNext = s.tick + C.cone.every;
    w.cone = s.tick + C.cone.warn;
    w.cx = h.x;
    w.cy = h.y;
    ev(s, { t: 'senda', kind: 'coneWarn', npc: '', x: boss.x, y: boss.y, n: C.cone.warn });
  }
  if (w.cone >= 0 && s.tick >= w.cone) {
    w.cone = -1;
    const ax = w.cx - boss.x;
    const ay = w.cy - boss.y;
    ev(s, { t: 'senda', kind: 'cone', npc: '', x: w.cx, y: w.cy });
    for (const x of s.heroes) {
      if (x.downTicks > 0) continue;
      const hx = x.x - boss.x;
      const hy = x.y - boss.y;
      const dot = hx * ax + hy * ay;
      const cross = hx * ay - hy * ax;
      if (dot > 0 && withinRange(x.x, x.y, boss.x, boss.y, C.cone.len) && Math.abs(cross) * 10000 <= dot * C.cone.half) damageHero(s, x, mulBp(x.stats.maxHp, C.cone.bp), boss, 'physical', true);
    }
  }
  // Its bite (two or one): round where the hero stood.
  if (h && left >= 1 && left <= 2 && w.bite < 0 && s.tick >= w.biteNext) {
    w.biteNext = s.tick + C.bite.every;
    w.bite = s.tick + C.bite.warn;
    w.bx = h.x;
    w.by = h.y;
    ev(s, { t: 'senda', kind: 'biteWarn', npc: '', x: h.x, y: h.y, n: C.bite.warn });
  }
  if (w.bite >= 0 && s.tick >= w.bite) {
    w.bite = -1;
    ev(s, { t: 'senda', kind: 'bite', npc: '', x: w.bx, y: w.by });
    for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, w.bx, w.by, C.bite.r)) damageHero(s, x, mulBp(x.stats.maxHp, C.bite.bp), boss, 'poison', true);
  }
  // Its trail of rot while it crawls.
  if (left <= 2 && s.tick >= w.trailNext) {
    w.trailNext = s.tick + C.trail.every;
    const last = w.trail[w.trail.length - 1];
    if (!last || !withinRange(last[0] as number, last[1] as number, boss.x, boss.y, 500)) w.trail.push([boss.x, boss.y, s.tick + C.trail.last]);
  }
  for (let i = w.trail.length - 1; i >= 0; i--) if (s.tick >= ((w.trail[i] as number[])[2] as number)) w.trail.splice(i, 1);
  if (w.trail.length > 16) w.trail.splice(0, w.trail.length - 16);
  // The decision: the Grandsire's branch squeezing her, or the seed carried under her swipes.
  if (C.healed) {
    if (s.tick >= w.branchNext) {
      w.branchNext = s.tick + C.branch.every;
      if (s.waveT >= C.branch.every) {
        boss.hp = imax(1, boss.hp - mulBp(boss.maxHp, C.branch.bp));
        ev(s, { t: 'senda', kind: 'branch', npc: '', x: boss.x, y: boss.y });
        cue(`branch${idiv(s.waveT, C.branch.every)}`);
      }
    }
  } else if (h) {
    if (w.swipe < 0 && s.waveT >= C.swipe.first && s.tick >= w.swipeNext) {
      w.swipeNext = s.tick + C.swipe.every;
      w.swipe = s.tick + C.swipe.warn;
      w.sx = h.x;
      w.sy = h.y;
      ev(s, { t: 'senda', kind: 'swipeWarn', npc: '', x: h.x, y: h.y, n: C.swipe.warn });
    }
    if (w.swipe >= 0 && s.tick >= w.swipe) {
      w.swipe = -1;
      ev(s, { t: 'senda', kind: 'swipe', npc: '', x: w.sx, y: w.sy });
      if (withinRange(h.x, h.y, w.sx, w.sy, C.swipe.r)) {
        damageHero(s, h, mulBp(h.stats.maxHp, C.swipe.heroBp), boss, 'physical', true);
        w.seedHits++;
        ev(s, { t: 'senda', kind: 'seedHit', npc: '', x: h.x, y: h.y, n: w.seedHits });
        cue(`seedHit${w.seedHits}`);
        if (w.seedHits >= C.seed) w.lost = 3;
      }
    }
  }
  // Slimes out of the walls, for the colliers; the colliers chop what reaches them.
  // (Three out of the walls at most: what the colliers don't chop, the hero must.)
  if (s.waveT >= C.slimes.first && s.waveT % C.slimes.every === 0 && Object.keys(w.sl).length < 3) {
    for (let k = 0; k < C.slimes.n; k++) {
      const p = C.holes[(idiv(s.tick, C.slimes.every) + k) % C.holes.length] as Pt;
      const e = spawn(s, 'rootwight', p.x, p.y);
      w.sl[e.id] = (idiv(s.tick, C.slimes.every) + k) % 3;
    }
  }
  if (s.tick % C.chop.every === 0) {
    const people = s.senda?.npcs.filter((n) => n.hp > 0 && (n.id === 'ruda' || n.id.startsWith('collier'))) ?? [];
    for (const e of s.enemies) {
      if (e.hp <= 0 || e.id === w.boss) continue;
      if (!people.some((n) => withinRange(n.x, n.y, e.x, e.y, 1100))) continue;
      e.hp -= imax(1, mulBp(e.maxHp, C.chop.bp));
      ev(s, { t: 'senda', kind: 'chop', npc: '', x: e.x, y: e.y });
      if (e.hp <= 0) e.hp = 0;
    }
  }
  for (const id of Object.keys(w.sl)) {
    const e = findEnemy(s, Number(id));
    if (!e || e.hp <= 0) delete w.sl[Number(id)];
  }
  // Soft time: the floor rots in from the walls.
  if (s.waveT >= C.rot.at) {
    const span = imax(1, C.rot.half - C.rot.at);
    // (Half the floor: the circle's radius over √2, 7071 bp of it.)
    const shrink = idiv((C.rot.r - mulBp(C.rot.r, 7071)) * (s.waveT - C.rot.at), span);
    w.rotR = imax(1800, C.rot.r - shrink);
    if (s.tick % 12 === 0)
      for (const x of s.heroes) if (x.downTicks === 0 && !withinRange(x.x, x.y, C.mid.x, C.mid.y + 1700, w.rotR)) damageHero(s, x, mulBp(x.stats.maxHp, C.rot.bp), null, 'poison', true);
  }
  if (w.gate >= 0 && s.tick >= w.gateUntil) w.gate = -1;
};

/** The Carcoma's own way: still with three or four roots; at the hero with two or one; bare, to the door. */
const carcomaHold = (s: BattleState, C: Car, w: St, e: Enemy): boolean => {
  e.stun = 0;
  if (w.done >= 0 || w.lost) {
    e.anim = 'idle';
    return true;
  }
  const left = feeding(w);
  const h = heroOf(s);
  if (left >= 3) {
    e.anim = w.cone >= 0 ? 'attack' : 'idle';
    return true;
  }
  if (left >= 1) {
    if (w.bite >= 0) {
      e.anim = 'attack';
      return true;
    }
    // (Still held by her roots: no farther from the middle than `tether`.)
    const tx = h ? h.x : e.x;
    const ty = h ? h.y : e.y;
    const dx = tx - C.mid.x;
    const dy = ty - C.mid.y;
    const d = imax(1, Math.abs(dx) + Math.abs(dy));
    const gx = d > C.tether ? C.mid.x + idiv(dx * C.tether, d) : tx;
    const gy = d > C.tether ? C.mid.y + idiv(dy * C.tether, d) : ty;
    if (h && !withinRange(e.x, e.y, gx, gy, 300) && !withinRange(e.x, e.y, h.x, h.y, 1300)) {
      e.anim = 'walk';
      stepToward(e, gx, gy, C.crawl);
    } else e.anim = 'idle';
    return true;
  }
  // Bare: to the door, along the way down the middle; a gate of sound root shut across it stops her.
  const g = w.gate >= 0 ? (C.gates[w.gate] as { bar: Pt }) : null;
  if (g && e.y < g.bar.y && e.y >= g.bar.y - 1500) {
    e.anim = 'idle';
    return true;
  }
  e.anim = 'walk';
  // First onto the middle line, then straight down it.
  if (Math.abs(e.x - C.door.x) > 200) stepToward(e, C.door.x, e.y + 400, C.run);
  else stepToward(e, C.door.x, C.door.y, C.run);
  if (withinRange(e.x, e.y, C.door.x, C.door.y, C.doorR)) {
    w.lost = 2;
    ev(s, { t: 'senda', kind: 'atDoor', npc: '', x: e.x, y: e.y });
  }
  return true;
};

/** A slime out of the walls: to its collier at the door (the hero close by, it turns on the hero). */
const slimeHold = (s: BattleState, w: St, e: Enemy, px: number): boolean => {
  const h = heroOf(s);
  if (h && withinRange(h.x, h.y, e.x, e.y, 1500)) return false;
  const people = s.senda?.npcs.filter((n) => n.hp > 0 && (n.id === 'ruda' || n.id.startsWith('collier'))) ?? [];
  const want = people[(w.sl[e.id] as number) % imax(1, people.length)] ?? h;
  if (!want) return false;
  if (withinRange(e.x, e.y, want.x, want.y, 700)) {
    e.anim = 'attack';
    return true;
  }
  e.anim = 'walk';
  stepToward(e, want.x, want.y, px);
  return true;
};

/* ---------- the act ---------- */

export const deepTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.deep;
  const sr = s.senda;
  if (!r || !sr) return;
  if (!sr.deep) {
    const w = fresh();
    sr.deep = w;
    const C = r.carcoma;
    if (C) {
      const e = spawnEnemy(s, 'drowned', 1, false, 0, C.mid.x, C.mid.y, C.hp);
      w.boss = e.id;
      s.bossId = e.id;
      w.roots = r.nets.map((n) => rootsOf(n));
      w.cut = [0, 0, 0, 0];
      w.resin = C.patches.map(() => 0);
      w.catchAt = C.patches.map(() => -1);
      w.wind = C.wind;
      w.hcut = C.hidden.cells.map(() => 0);
      w.closed = C.frost.knots.map(() => 0);
      w.buds = C.frost.nodes.map((n) => (n.need ? -1 : -2));
      w.crackUntil = C.frost.nodes.map(() => 0);
      w.pulseNext = C.pulse;
      w.coneNext = C.cone.first;
      w.swipeNext = C.swipe.first;
      w.gateUsed = C.gates.map(() => 0);
      // The tries before left their scars: roots already cut as it starts (in the order they would grow back).
      for (let k = 0; k < C.pre; k++) {
        const root = C.regrowOrder[3 - k] as number;
        w.cut[root] = 1;
        w.cuts++;
      }
    }
  }
  const w = sr.deep;
  if (!s.started) return;
  if (w.pend.length) {
    for (const c of w.pend) cue(c);
    w.pend.length = 0;
  }
  if (r.mouths) mouthsTick(s, r, r.mouths, w, cue);
  else if (r.carcoma) carcomaTick(s, r, r.carcoma, w, cue);
  w.t++;
};

/** Moved by the act's own rule (true): the Carcoma, the slimes for the colliers. */
export const deepHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.deep;
  const w = s.senda?.deep;
  if (!r?.carcoma || !w) return false;
  if (e.id === w.boss) return carcomaHold(s, r.carcoma, w, e);
  if (w.sl[e.id] !== undefined) return slimeHold(s, w, e, px(e));
  return false;
};

/** What lands no blow on the path's people by standing at them: the Carcoma (its blows are its own). */
export const deepSpares = (s: BattleState, e: Enemy): boolean => !!s.senda?.deep && e.id === s.senda.deep.boss;

/** What the Carcoma takes of a blow: nothing with four roots, a quarter more for each one cut. */
export const deepTakes = (s: BattleState, e: Enemy, dmg: number): number => {
  const C = s.rules?.senda?.deep?.carcoma;
  const w = s.senda?.deep;
  if (!C || !w || e.id !== w.boss) return dmg;
  return mulBp(dmg, C.takenBp[imin(4, w.cut.filter(Boolean).length)] ?? 10000);
};

/** The hero's pace: in the Carcoma's trail of rot it wades. */
export const deepSpeedBp = (s: BattleState, h: Hero): number => {
  const C = s.rules?.senda?.deep?.carcoma;
  const w = s.senda?.deep;
  if (!C || !w) return 10000;
  for (const p of w.trail) if (withinRange(h.x, h.y, p[0] as number, p[1] as number, C.trail.r)) return 10000 - C.trail.slowBp;
  return 10000;
};

/** A tap on a mark: the hero goes to stand by it (and its Raíz, or the ember, does the rest). */
export const deepOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.deep;
  const w = s.senda?.deep;
  if (!r || !w || w.done >= 0 || at < 0) return;
  const m = r.marks[at];
  if (!m) return;
  w.job = at;
  w.held = 0;
  h.target = 0;
  h.forced = false;
  let x = m.x;
  let y = m.y;
  if (m.k === 'tangle') {
    const st = w.roots[m.i];
    const p = st && st.knot >= 0 ? (r.nets[m.i] as { nodes: Pt[] }).nodes[st.knot] : undefined;
    if (p) {
      x = p.x;
      y = p.y;
    }
  }
  // (Sowing and lighting reach from afar: only as near as it needs.)
  const far = m.k === 'stone' ? (r.mouths?.sow ?? r.carcoma?.resin.reach ?? 0) : m.k === 'patch' ? (r.carcoma?.resin.reach ?? 0) : 0;
  if (far && withinRange(h.x, h.y, x, y, far - 300)) {
    orderMove(s, h, h.x, h.y);
    return;
  }
  if (far) {
    const dx = h.x - x;
    const dy = h.y - y;
    const d = imax(1, idiv(Math.abs(dx) + Math.abs(dy), 1));
    const k = far - 600;
    if (d > k) {
      x += idiv(dx * k, d);
      y += idiv(dy * k, d);
    }
  }
  orderMove(s, h, x, y);
};

/** Another order of the hero's own: the mark it was sent to is off. */
export const deepMarkOff = (s: BattleState): void => {
  const w = s.senda?.deep;
  if (w) w.job = -1;
};

/** How far the goal has come: the mouths open and clean; the Carcoma's life taken. */
export const deepGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.deep;
  const w = s.senda?.deep;
  if (!r || !w) return { done: 0, need: 1 };
  if (r.mouths) {
    const n = r.mouths.mouths.length;
    if (w.done >= 0) return { done: n * 2, need: n * 2 };
    return { done: w.open.filter((x) => x >= 0).length + w.clean.filter((x) => x >= 0).length, need: n * 2 };
  }
  const b = findEnemy(s, w.boss);
  if (!b) return { done: w.done >= 0 ? 1 : 0, need: 1 };
  return { done: b.maxHp - imax(0, b.hp), need: b.maxHp };
};

/** Won a moment after the goal; lost at the door or with the seed broken. */
export const deepResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.deep;
  if (!w) return null;
  if (w.lost) return 'lost';
  if (w.done >= 0) return s.tick >= w.done + 36 ? 'won' : null;
  return null;
};

/** The mark nearest a point within `d` that can be worked now (the HUD's offer), −1 if none. */
export const deepMarkNear = (s: BattleState, x: number, y: number, d: number): number => {
  const r = s.rules?.senda?.deep;
  const w = s.senda?.deep;
  if (!r || !w) return -1;
  let best = -1;
  let bd = d * d;
  r.marks.forEach((m, i) => {
    if (!deepMarkLive(s, i)) return;
    let mx = m.x;
    let my = m.y;
    if (m.k === 'tangle') {
      const st = w.roots[m.i];
      const p = st && st.knot >= 0 ? (r.nets[m.i] as { nodes: Pt[] }).nodes[st.knot] : undefined;
      if (!p) return;
      mx = p.x;
      my = p.y;
    }
    const dd = (mx - x) * (mx - x) + (my - y) * (my - y);
    if (dd < bd) {
      bd = dd;
      best = i;
    }
  });
  return best;
};

/** Whether a mark can be worked now (a stone of a shut mouth, a tangle that isn't, a patch burnt, a root already cut: no). */
export const deepMarkLive = (s: BattleState, i: number): boolean => {
  const r = s.rules?.senda?.deep;
  const w = s.senda?.deep;
  const m = r?.marks[i];
  if (!r || !w || !m || w.done >= 0) return false;
  if (r.mouths) {
    if (m.k === 'knot') return true;
    const open = (w.open[m.i] ?? -1) >= 0 && (w.clean[m.i] ?? -1) < 0;
    const st = w.roots[m.i];
    if (m.k === 'stone') return open && !!st && !(st.sprout & (1 << m.j)) && st.knot < 0;
    if (m.k === 'tangle') return open && !!st && st.knot >= 0;
    return false;
  }
  if (m.k === 'stone') return !w.cut[MUD] && !!w.roots[0] && !((w.roots[0] as RootsState).sprout & (1 << m.j)) && (w.roots[0] as RootsState).knot < 0;
  if (m.k === 'tangle') return !w.cut[MUD] && !!w.roots[0] && (w.roots[0] as RootsState).knot >= 0;
  if (m.k === 'patch') return !w.cut[RESIN] && w.resin[m.i] === 0;
  if (m.k === 'cell') return !w.cut[HIDDEN] && !w.hcut[m.i];
  if (m.k === 'knot') return !w.cut[FROST];
  if (m.k === 'gate') return w.bare >= 0 && !w.gateUsed[m.i];
  return false;
};
